import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Badge, Button, Card, EmptyState, ErrorAlert, Field, Pagination, Spinner, StatCard } from '../components/ui';
import { usePagination } from '../lib/usePagination';

type Any = Record<string, any>;

export function NieboGuardAlertsSection() {
  const [status, setStatus] = useState<Any | null>(null);
  const [settings, setSettings] = useState<Any | null>(null);
  const [inbox, setInbox] = useState<Any[]>([]);
  const [items, setItems] = useState<Any[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [message, setMessage] = useState('');
  const itemPage = usePagination(items, 10);
  const inboxPage = usePagination(inbox, 10);

  const load = useCallback(async () => {
    setError(null);
    const [nextStatus, nextInbox, nextItems] = await Promise.all([
      api.nieboguard.alerts.status(),
      api.nieboguard.alerts.inbox(40),
      api.nieboguard.alerts.list({ limit: 80 }),
    ]);
    setStatus(nextStatus);
    setSettings(nextStatus.settings);
    setInbox(nextInbox.inbox ?? []);
    setItems(nextItems.items ?? []);
  }, []);

  useEffect(() => { load().catch(setError); }, [load]);

  const act = async (key: string, action: () => Promise<unknown>, success: string) => {
    setBusy(key); setError(null); setMessage('');
    try { await action(); setMessage(success); await load(); }
    catch (err) { setError(err); }
    finally { setBusy(null); }
  };

  const update = (path: string, value: unknown) => setSettings((current) => {
    if (!current) return current;
    const [head, ...rest] = path.split('.');
    if (!head) return current;
    if (!rest.length) return { ...current, [head]: value };
    return current;
  });
  const save = () => act('save', () => api.nieboguard.alerts.saveSettings(settings ?? {}), 'Ustawienia mostu X zapisane.');
  const process = () => act('process', () => api.nieboguard.alerts.process(8), 'Kolejka X przetworzona.');

  if (!settings) return error ? <ErrorAlert error={error} /> : <Spinner label="Ładowanie mostu X…" />;
  const counts = status?.counts ?? {};
  const configured = status?.configured ?? {};

  return <div className="nb-list">
    {error ? <ErrorAlert error={error} /> : null}
    {message ? <div className="alert alert--success">{message}</div> : null}

    <div className="nb-hero">
      <div className="nb-row nb-row--between">
        <div>
          <h2>Alerty X przez Kontentowo</h2>
          <p>NieboGuard pozostaje źródłem prawdy. Ten most przekazuje podpisane zdarzenia do Kontentowo, a Kontentowo publikuje przez istniejące konto X i Sociamonials. NieboGuard nie przechowuje tokenów X ani Sociamonials.</p>
        </div>
        <Badge tone={settings.enabled ? 'success' : 'warning'}>{settings.enabled ? 'Most włączony' : 'Most wyłączony'}</Badge>
      </div>
      <div className="nb-row">
        <Badge tone={configured.secret ? 'success' : 'danger'}>{configured.secret ? 'HMAC ustawiony' : 'Brak HMAC'}</Badge>
        <Badge tone={settings.test_mode ? 'warning' : 'neutral'}>{settings.test_mode ? 'Dry-run' : 'Publikacja rzeczywista'}</Badge>
        <Badge tone={settings.test_mode ? 'warning' : 'neutral'}>{settings.test_mode ? 'Tryb testowy' : 'Tryb normalny'}</Badge>
        <Badge tone={configured.x_account ? 'success' : 'danger'}>{configured.x_account ? 'Konto X aktywne' : 'Brak konta X'}</Badge>
      </div>
    </div>

    <div className="nb-grid nb-grid--stats">
      <StatCard label="Wysłane" value={counts.sent ?? 0} />
      <StatCard label="Oczekuje" value={counts.pending ?? 0} />
      <StatCard label="Do zatwierdzenia" value={counts.pending_approval ?? 0} />
      <StatCard label="Błędy" value={(counts.failed ?? 0) + (counts.dead ?? 0)} />
      <StatCard label="Odrzucone filtry" value={counts.skipped ?? 0} />
    </div>

    <Card title="Konfiguracja mostu" actions={<><Button size="sm" variant="ghost" disabled={busy === 'process'} onClick={process}>Przetwórz kolejkę</Button><Button size="sm" onClick={save} disabled={busy === 'save'}>{busy === 'save' ? 'Zapisuję…' : 'Zapisz'}</Button></>}>
      <div className="nb-grid">
        <div className="nb-list">
          <label className="nb-check"><input type="checkbox" checked={!!settings.enabled} onChange={(e) => update('enabled', e.target.checked)} /> Most X włączony</label>
          <label className="nb-check"><input type="checkbox" checked={!!settings.auto_publish} onChange={(e) => update('auto_publish', e.target.checked)} /> Automatyczna publikacja</label>
          <label className="nb-check"><input type="checkbox" checked={!!settings.require_approval} onChange={(e) => update('require_approval', e.target.checked)} /> Wymagaj zatwierdzenia</label>
          <label className="nb-check"><input type="checkbox" checked={!!settings.publish_updates} onChange={(e) => update('publish_updates', e.target.checked)} /> Publikuj aktualizacje</label>
          <label className="nb-check"><input type="checkbox" checked={!!settings.publish_cancellations} onChange={(e) => update('publish_cancellations', e.target.checked)} /> Publikuj odwołania</label>
        </div>
        <div className="nb-list">
          <label className="nb-check"><input type="checkbox" checked={!!settings.include_image} onChange={(e) => update('include_image', e.target.checked)} /> Dodawaj grafikę</label>
          <label className="nb-check"><input type="checkbox" checked={!!settings.include_link} onChange={(e) => update('include_link', e.target.checked)} /> Dodawaj link</label>
          <label className="nb-check"><input type="checkbox" checked={!!settings.test_mode} onChange={(e) => update('test_mode', e.target.checked)} /> Tryb testowy (bez publikacji)</label>
          <Field label="Język">
            <select value={settings.language} onChange={(e) => update('language', e.target.value)}>
              <option value="pl">PL</option><option value="en">EN</option><option value="uk">UK</option>
            </select>
          </Field>
          <Field label="Minimalna ważność">
            <select value={settings.min_severity} onChange={(e) => update('min_severity', e.target.value)}>
              {['INFO', 'ADVISORY', 'WARNING', 'SEVERE', 'CRITICAL'].map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </Field>
        </div>
      </div>
      <div className="nb-grid">
        <Field label="Kategorie"><input value={(settings.categories ?? []).join(',')} onChange={(e) => update('categories', e.target.value.split(',').map(x => x.trim()).filter(Boolean))} /></Field>
        <Field label="Regiony (puste = wszystkie)"><input value={(settings.regions ?? []).join(',')} onChange={(e) => update('regions', e.target.value.split(',').map(x => x.trim()).filter(Boolean))} /></Field>
        <Field label="Limit / 24 h"><input type="number" min={1} max={200} value={settings.daily_limit} onChange={(e) => update('daily_limit', Number(e.target.value))} /></Field>
      </div>
      <p className="fineprint">Oficjalne alerty mogą omijać próg ważności; kategoria i region nadal muszą pasować. Testy nie tworzą publicznych postów.</p>
    </Card>

    <Card title="Historia publikacji X" actions={<Button size="sm" variant="ghost" onClick={() => load()}>Odśwież</Button>}>
      {items.length === 0 ? <EmptyState title="Brak zdarzeń X" hint="Po odebraniu alertu z NieboGuard pojawi się tutaj wpis." /> :
        <div className="nb-scroll"><table className="table">
          <thead><tr><th>Czas</th><th>Zdarzenie</th><th>Akcja</th><th>Status</th><th>Próby</th><th>Tekst</th><th>Błąd / ID</th><th>Akcje</th></tr></thead>
          <tbody>{itemPage.pageRows.map((item) => <tr key={item.id}>
            <td className="nb-muted">{String(item.created_at ?? '—').slice(0, 16).replace('T', ' ')}</td>
            <td><code>{item.event_id}</code><br /><small className="nb-muted">{item.revision}</small></td>
            <td>{item.action}</td>
            <td><Badge tone={['sent'].includes(item.status) ? 'success' : ['failed', 'dead'].includes(item.status) ? 'danger' : ['pending_approval'].includes(item.status) ? 'warning' : 'info'}>{item.status}</Badge></td>
            <td>{item.attempts ?? 0}</td>
            <td className="nb-text-preview">{item.text || '—'}</td>
            <td><small className={item.last_error ? 'nb-danger' : 'nb-muted'}>{item.last_error || item.external_id || '—'}</small></td>
            <td className="nb-actions">
              {item.status === 'pending_approval' ? <Button size="sm" onClick={() => act(`approve-${item.id}`, () => api.nieboguard.alerts.approve(item.id), 'Alert zatwierdzony.')}>Zatwierdź</Button> : null}
              {['failed', 'dead', 'superseded', 'cancelled', 'test'].includes(item.status) ? <Button size="sm" variant="ghost" onClick={() => act(`retry-${item.id}`, () => api.nieboguard.alerts.retry(item.id), 'Ponowienie zakolejkowane.')}>Ponów</Button> : null}
              {['pending', 'pending_approval'].includes(item.status) ? <Button size="sm" variant="ghost" onClick={() => act(`cancel-${item.id}`, () => api.nieboguard.alerts.cancel(item.id), 'Publikacja anulowana.')}>Anuluj</Button> : null}
            </td>
          </tr>)}</tbody>
        </table><Pagination page={itemPage.page} pageSize={itemPage.pageSize} total={itemPage.total} onChange={itemPage.setPage} /></div>}
    </Card>

    <Card title="Odebrane zdarzenia" actions={<span className="nb-muted">Ostatnie {inbox.length}</span>}>
      {inbox.length === 0 ? <EmptyState title="Brak odebranych zdarzeń" hint="Intake zapisuje każdy podpisany alert, także odfiltrowany." /> :
        <div className="nb-scroll"><table className="table">
          <thead><tr><th>Odebrano</th><th>Event</th><th>Akcja</th><th>Rewizja</th><th>Status</th><th>Powód</th></tr></thead>
          <tbody>{inboxPage.pageRows.map((row) => <tr key={row.id}>
            <td>{String(row.received_at ?? '').slice(0, 16).replace('T', ' ')}</td>
            <td><code>{row.event_id}</code></td><td>{row.action}</td><td>{row.revision}</td>
            <td>{row.status}</td><td>{row.reason || '—'}</td>
          </tr>)}</tbody>
        </table><Pagination page={itemPage.page} pageSize={itemPage.pageSize} total={itemPage.total} onChange={itemPage.setPage} /></div>}
    </Card>
  </div>;
}
